import React, { useState } from 'react';
import CheckboxField from '../../../../Components/Form/components/CheckboxField';
import FormCondition from '../../../../Components/Form/FormCondition';
import FormControl from '../../../../Components/Form/FormControl';
import Select, { SelectValue } from '../../../../Components/Base/Select/Select';
import MultiSelect from '../../../../Components/Base/MultiSelect/MultiSelect';
import DatePicker from '../../../../Components/Base/DatePicker/DatePicker';
import TimePickerField from '../../../../Components/Form/components/TimePickerField/TimePickerField';
import InputField from '../../../../Components/Form/components/InputField';
import RadioListField from '../../../../Components/Form/components/RadioListField';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import RemoveButton from '../../../../Components/Button/RemoveButton';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Moment } from 'moment';
import { RadioListItem } from '../../../../Components/Base/RadioList/RadioList';
import {
	useLazyNormalWorkplacesQuery,
	useLazyWorkplaceDoctorsQuery,
	useLazyWorkplaceMembersQuery,
} from '../../../../graphql/graphql';
import { useEffectAsync } from '../../../../helpers';
import { getAbsenceEventValidationScheme } from '../../Services/absenceEventValidationScheme.service';
import Text from '../../../../Components/Base/Text/Text';
// @ts-ignore
import styles from './CalendarAbsenceEventForm.module.scss';

export interface CalendarAbsenceEventDefaultValues {
	dateFrom: Moment;
	dateTo: Moment;
	timeFrom?: Moment;
	timeTo?: Moment;
	workplaceMember?: SelectValue;
	reason?: string;
	wholeDay?: boolean;
	wholeWorkplace: boolean;
	allowContactInAbsence: boolean;
}

export interface CalendarAbsenceEventFormValues {
	dateFrom: Moment;
	dateTo: Moment;
	timeFrom?: Moment;
	timeTo?: Moment;
	allowContactInAbsence: boolean;
	wholeDay: boolean;
	workplaceMember?: SelectValue;
	workplaces?: SelectValue[];
	reason: string;
	wholeWorkplace: boolean;
}

interface Props {
	onSubmit: (values: CalendarAbsenceEventFormValues) => void;
	onRemove: () => void;
	defaultValues?: CalendarAbsenceEventDefaultValues;
	isEdit?: boolean;
}

const ALLOW_CONTACT_OPTIONS: RadioListItem[] = [
	{
		title: 'base.yes',
		value: true,
	},
	{
		title: 'base.no',
		value: false,
	},
];

const CalendarAbsenceEventForm: React.FC<Props> = ({
	onSubmit,
	onRemove,
	defaultValues,
	isEdit = false,
}) => {
	const [selectedUserWorkplaces, setSelectedUserWorkplaces] = useState<
		SelectValue[]
	>([]);
	const [loggedUserWorkplacesIds, setLoggedUserWorkplacesIds] = useState<
		string[]
	>([]);
	const [workplaceMembers, setWorkplaceMembers] = useState<SelectValue[]>([]);

	const [getWorkplaceMembers] = useLazyWorkplaceMembersQuery();
	const [getWorkplaces] = useLazyNormalWorkplacesQuery();
	const [getWorkplaceDoctors] = useLazyWorkplaceDoctorsQuery();

	const formMethods = useForm({
		resolver: yupResolver(
			getAbsenceEventValidationScheme(
				isEdit,
				defaultValues?.wholeWorkplace || false,
			),
		),
		defaultValues,
	});

	const wholeWorkplace = formMethods.watch('wholeWorkplace');

	const handleSelectWorkplaceMember = async (value: SelectValue) => {
		const tempWorkplaces: SelectValue[] = [];

		const { data } = await getWorkplaceMembers({
			userId: value.value as string,
			workplaceIds: loggedUserWorkplacesIds,
		});

		(data?.workplaceMembers?.data || []).forEach(workplace => {
			tempWorkplaces.push({
				value: workplace.id,
				label: workplace.workplace.doctorDisplayName,
			});
		});

		setSelectedUserWorkplaces(tempWorkplaces);
	};

	const handleLoadLoggedUserWorkplaces = async () => {
		const tempWorkplacesIds: string[] = [];

		const { data } = await getWorkplaces();

		(data?.workplaces?.data || []).forEach(workplace => {
			tempWorkplacesIds.push(workplace.id);
		});

		setLoggedUserWorkplacesIds(tempWorkplacesIds);
	};

	const handleLoadWorkplaceMembers = async () => {
		const tempWorkplaceMembers: SelectValue[] = [];

		const { data } = await getWorkplaceDoctors({
			workplaceIds: loggedUserWorkplacesIds,
			roleKeys: ['doctor', 'nurse', 'therapist'],
			take: 999,
		});

		(data?.workplaceDoctors?.data || []).forEach(workplaceDoctor => {
			const alreadyAdded = tempWorkplaceMembers.some(
				w => w.value === workplaceDoctor?.user?.id,
			);
			if (!alreadyAdded) {
				tempWorkplaceMembers.push({
					value: workplaceDoctor.user.id,
					label: workplaceDoctor.user.profile.fullName,
				});
			}
		});

		setWorkplaceMembers(tempWorkplaceMembers);
	};

	useEffectAsync(async () => {
		await handleLoadLoggedUserWorkplaces();
	}, []);

	useEffectAsync(async () => {
		await handleLoadWorkplaceMembers();
	}, [loggedUserWorkplacesIds]);

	return (
		<FormWrapper formMethods={formMethods}>
			<div>
				<div style={{ display: 'none' }}>
					<CheckboxField name='wholeWorkplace' />
				</div>
				<div>
					<Text
						tx={
							wholeWorkplace
								? 'component.calendar.absence.forWorkplace'
								: 'component.calendar.absence.forWorkplaceMember'
						}
						className={styles.infoText}
					/>
				</div>
				<FormCondition controlName='wholeWorkplace' equal={false}>
					<FormControl
						label='base.selectWorker'
						name='workplaceMember'
						onChange={handleSelectWorkplaceMember}
						isRequired>
						<Select
							options={workplaceMembers}
							placeholder='base.shared.pick'
							isSearchable
						/>
					</FormControl>
					{!isEdit && (
						<>
							<FormControl
								label='base.chooseWorkplace'
								name='workplaces'
								isRequired>
								<MultiSelect
									options={selectedUserWorkplaces}
									isSearchable
									placeholder='base.chooseWorkplace'
									noOptionsMessage='base.pickWorkerFist'
								/>
							</FormControl>
						</>
					)}
				</FormCondition>
				{!isEdit && (
					<CheckboxField name='wholeDay' tx='base.wholeDay' isWrapped />
				)}
				<div className={'row'}>
					<div className={'col-6'}>
						<FormControl
							label='base.shared.dateFrom'
							name='dateFrom'
							isRequired>
							<DatePicker />
						</FormControl>
						<FormControl label='base.shared.dateTo' name='dateTo' isRequired>
							<DatePicker />
						</FormControl>
					</div>
					<div className={'col-6'}>
						<FormCondition controlName='wholeDay' equal={false}>
							<TimePickerField
								name='timeFrom'
								label='base.shared.timeFrom'
								isRequired
								isWrapped
							/>
							<TimePickerField
								name='timeTo'
								label='base.shared.timeTo'
								isRequired
								isWrapped
							/>
						</FormCondition>
					</div>
				</div>
				<InputField
					label='component.calendar.absence.reason'
					name='reason'
					isRequired
					isWrapped
				/>
				<RadioListField
					label='component.calendar.absence.allowContactInAbsence2'
					name='allowContactInAbsence'
					data={ALLOW_CONTACT_OPTIONS}
					isWrapped
					isRequired
					inLine
				/>
				<div
					className={'d-flex flex-row align-items-center mt-4'}
					style={{ gap: '15px' }}>
					<SubmitButton
						onSubmit={values =>
							onSubmit(values as unknown as CalendarAbsenceEventFormValues)
						}
						title={isEdit ? 'base.shared.edit' : 'base.create'}
					/>
					<RemoveButton onClick={onRemove} disableFlashMessage />
				</div>
			</div>
		</FormWrapper>
	);
};

export default CalendarAbsenceEventForm;
